// Storage for the Supabase session that is shared by afterhourz.tv and its subdomains
// (studio.afterhourz.tv in particular), so signing in on one signs you in on both.
//
// localStorage is per-origin, so the session lives in cookies scoped to
// Domain=.afterhourz.tv. Browsers cap cookies at ~4 KB, so values are split into
// chunks (<key>.0, <key>.1, … plus <key>.n with the count).
//
// The cookies must be readable by JavaScript (supabase-js manages the session in
// the browser), so they are not HttpOnly. The Worker never uses them for auth
// (APIs take a Bearer token), so they don't open CSRF. media.afterhourz.tv
// serves files with a sandbox CSP so uploaded content can't run script and read them.
// Off afterhourz.tv (localhost, previews) this falls back to plain localStorage.

import type { SupportedStorage } from '@supabase/supabase-js';

const SITE_DOMAIN = 'afterhourz.tv';
const CHUNK_SIZE = 3000; // characters per cookie, after encoding
const MAX_CHUNKS = 8;
const MAX_AGE_S = 400 * 24 * 60 * 60; // browsers' maximum cookie lifetime

/** ".afterhourz.tv" on the real site, or null elsewhere (localhost, workers.dev). */
export function sharedCookieDomain(): string | null {
  const host = location.hostname;
  return host === SITE_DOMAIN || host.endsWith(`.${SITE_DOMAIN}`) ? `.${SITE_DOMAIN}` : null;
}

export function readCookie(name: string): string | null {
  for (const part of document.cookie.split('; ')) {
    const eq = part.indexOf('=');
    if (eq > 0 && part.slice(0, eq) === name) return part.slice(eq + 1);
  }
  return null;
}

export function writeCookie(name: string, value: string, maxAgeSeconds = MAX_AGE_S) {
  const domain = sharedCookieDomain();
  document.cookie = [
    `${name}=${value}`,
    domain ? `Domain=${domain}` : '',
    'Path=/',
    `Max-Age=${maxAgeSeconds}`,
    'SameSite=Lax',
    location.protocol === 'https:' ? 'Secure' : '',
  ].filter(Boolean).join('; ');
}

const deleteCookie = (name: string) => writeCookie(name, '', 0);

function safeLocal(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function removeChunks(key: string) {
  const n = Number(readCookie(`${key}.n`)) || 0;
  for (let i = 0; i < Math.max(n, MAX_CHUNKS); i++) if (readCookie(`${key}.${i}`) !== null) deleteCookie(`${key}.${i}`);
  deleteCookie(`${key}.n`);
}

const cookieStorage: SupportedStorage = {
  getItem(key) {
    const n = Number(readCookie(`${key}.n`)) || 0;
    if (n > 0) {
      let joined = '';
      for (let i = 0; i < n; i++) {
        const chunk = readCookie(`${key}.${i}`);
        if (chunk === null) return null; // incomplete write; treat as signed out
        joined += chunk;
      }
      try {
        return decodeURIComponent(joined);
      } catch {
        return null;
      }
    }
    // One-time migration: sessions saved in this origin's localStorage before
    // sharing was added move into the shared cookies.
    const local = safeLocal();
    const legacy = local?.getItem(key) ?? null;
    if (legacy !== null) {
      cookieStorage.setItem(key, legacy);
      if (readCookie(`${key}.n`)) local?.removeItem(key);
    }
    return legacy;
  },
  setItem(key, value) {
    removeChunks(key);
    const encoded = encodeURIComponent(value);
    const chunks = Math.ceil(encoded.length / CHUNK_SIZE);
    if (chunks > MAX_CHUNKS) {
      // Too big to share. Keep it on this origin rather than lose the session.
      console.warn(`Session too large to share across subdomains (${encoded.length} chars); using localStorage.`);
      safeLocal()?.setItem(key, value);
      return;
    }
    for (let i = 0; i < chunks; i++) writeCookie(`${key}.${i}`, encoded.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
    writeCookie(`${key}.n`, String(chunks));
  },
  removeItem(key) {
    removeChunks(key);
    safeLocal()?.removeItem(key);
  },
};

/** Storage for supabase-js: shared cookies on afterhourz.tv, localStorage elsewhere. */
export function sessionStorageAdapter(): SupportedStorage | undefined {
  return sharedCookieDomain() ? cookieStorage : undefined; // undefined → supabase-js default (localStorage)
}
